import { useEffect, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { accountIdSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { isDefined } from 'utils/ramda';

import { ExistingGuest, ExistingGuestionConnection, IGuestSearchProps } from './types';

const useGuestSearchForm = ({ onSubmit, onClose, guestPortalId }: IGuestSearchProps) => {
  const accountId = useSelector(accountIdSelector);
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries(['companions']);
  }, []);

  const {
    control,
    watch,
    handleSubmit,
    resetField,
    formState: { isSubmitting },
  } = useForm();
  const mainGuest = watch('mainGuest') as ExistingGuest | undefined;

  const loadGuests = useMemo(() => getGuestOptionLoader(accountId, guestPortalId), [accountId, guestPortalId]);
  const companions = useMemo(() => mainGuest?.connections || [], [mainGuest]);

  const showCompanions = isDefined(companions) && mainGuest;

  const submitHandler = handleSubmit(async (values) => {
    await onSubmit(prepareDataBeforeSave(values, companions));
    onClose();
  });

  const resetCompanionsOnGuestChange = () => resetField('companions', { defaultValue: {} });

  return {
    isSubmitting,
    loadGuests,
    mainGuest,
    control,
    companions,
    showCompanions,
    submitHandler,
    resetCompanionsOnGuestChange,
  };
};

export default useGuestSearchForm;

const decoratedAxiosGet = axiosGetAutoCancelDecorator();

const getGuestOptionLoader = (accountId: string, guestPortalId: string) => async (q: string) => {
  const response: AxiosResponse<{
    data: ExistingGuest[];
  }> = await decoratedAxiosGet(`/acc/${accountId}/guest_portals/${guestPortalId}/contacts?q=${q}`);

  return response.data.data.map((guest) => {
    const { title, subtitle } = guestToOptionAdapter(guest);

    return {
      name: title,
      label: title,
      id: guest,
      value: guest,
      subtitle,
    };
  });
};

export const guestToOptionAdapter = ({ firstName, lastName, middleName, recentTrip }: ExistingGuest) => {
  const title = `${firstName || ''} ${middleName || ''} ${lastName || ''}`;
  let subtitle = '';
  if (recentTrip) {
    const { startDate, endDate, countryNames } = recentTrip;
    subtitle = 'Last Trip:';
    if (Array.isArray(countryNames) && countryNames.length > 0) {
      subtitle += ` ${countryNames.join(' ')}`;
    }
    if (startDate && !endDate) {
      subtitle += ` (${startDate})`;
    } else if (!startDate && endDate) {
      subtitle += ` (${endDate})`;
    } else if (startDate && endDate) {
      subtitle += ` (${startDate} - ${endDate})`;
    }
  }

  return { title, subtitle };
};

export const connectionToOptionAdapter = ({ firstName, lastName, middleName }: ExistingGuestionConnection) => {
  return {
    title: `${firstName || ''} ${middleName || ''} ${lastName || ''}`,
  };
};

function prepareDataBeforeSave(data: Record<any, any>, companions: ExistingGuestionConnection[] = []) {
  // Filter companions based on the checkbox value
  const checkedCompanions = companions.filter((_, idx) => data.companions[idx]) || [];

  // Add fromExistingGuest property for BE
  return [data.mainGuest, ...checkedCompanions].map((guest) => ({ contact_id: guest.id }));
}
